import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { getImageUrl, formatPrice } from '@/components/PropertyCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Megaphone, Star, BarChart3, PlusCircle, Trash2, Pencil, Eye, MousePointerClick, Image, ExternalLink, Calendar, Building2, Search } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AD_POSITIONS = [
  { value: 'homepage_banner', label: 'Homepage Banner' },
  { value: 'search_sidebar', label: 'Search Sidebar' },
  { value: 'search_between', label: 'Between Search Results' },
  { value: 'detail_sidebar', label: 'Property Detail Sidebar' },
];

const FEATURED_POSITIONS = [
  { value: 'homepage', label: 'Homepage Featured' },
  { value: 'search_top', label: 'Search Results Top' },
];

export default function SuperAdminPage() {
  const { language } = useLanguage();
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };

  // State
  const [ads, setAds] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  // Forms
  const [showAdDialog, setShowAdDialog] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [adForm, setAdForm] = useState({ title: '', image_url: '', link_url: '', position: 'homepage_banner', business_name: '', business_email: '', campaign_id: '', is_active: true });
  const [showCampaignDialog, setShowCampaignDialog] = useState(false);
  const [campaignForm, setCampaignForm] = useState({ name: '', description: '', business_name: '', business_email: '', is_active: true });
  const [showFeaturedDialog, setShowFeaturedDialog] = useState(false);
  const [featuredForm, setFeaturedForm] = useState({ property_id: '', position: 'homepage', is_active: true });
  const [propertySearch, setPropertySearch] = useState('');

  const fetchData = async () => {
    try {
      const [adsRes, campRes, featRes, propsRes] = await Promise.all([
        axios.get(`${API}/superadmin/ads`, { headers }),
        axios.get(`${API}/superadmin/campaigns`, { headers }),
        axios.get(`${API}/superadmin/featured`, { headers }),
        axios.get(`${API}/properties?limit=50`),
      ]);
      setAds(adsRes.data.ads);
      setCampaigns(campRes.data.campaigns);
      setFeatured(featRes.data.featured);
      setProperties(propsRes.data.properties);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  // ===== AD HANDLERS =====
  const openAdDialog = (ad = null) => {
    if (ad) {
      setEditingAd(ad);
      setAdForm({ title: ad.title, image_url: ad.image_url, link_url: ad.link_url, position: ad.position, business_name: ad.business_name, business_email: ad.business_email, campaign_id: ad.campaign_id || '', is_active: ad.is_active });
    } else {
      setEditingAd(null);
      setAdForm({ title: '', image_url: '', link_url: '', position: 'homepage_banner', business_name: '', business_email: '', campaign_id: '', is_active: true });
    }
    setShowAdDialog(true);
  };

  const saveAd = async (e) => {
    e.preventDefault();
    try {
      if (editingAd) {
        await axios.put(`${API}/superadmin/ads/${editingAd.id}`, adForm, { headers });
        toast.success('Ad updated');
      } else {
        await axios.post(`${API}/superadmin/ads`, adForm, { headers });
        toast.success('Ad created');
      }
      setShowAdDialog(false);
      fetchData();
    } catch { toast.error('Failed to save ad'); }
  };

  const deleteAd = async (id) => {
    if (!window.confirm('Delete this ad?')) return;
    try { await axios.delete(`${API}/superadmin/ads/${id}`, { headers }); toast.success('Ad deleted'); fetchData(); }
    catch { toast.error('Failed'); }
  };

  const handleAdImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/upload`, formData, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setAdForm(f => ({ ...f, image_url: res.data.path }));
      toast.success('Image uploaded');
    } catch { toast.error('Upload failed'); }
  };

  // ===== CAMPAIGN HANDLERS =====
  const saveCampaign = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/superadmin/campaigns`, campaignForm, { headers });
      toast.success('Campaign created');
      setShowCampaignDialog(false);
      setCampaignForm({ name: '', description: '', business_name: '', business_email: '', is_active: true });
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const deleteCampaign = async (id) => {
    if (!window.confirm('Delete campaign and unlink all ads?')) return;
    try { await axios.delete(`${API}/superadmin/campaigns/${id}`, { headers }); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Failed'); }
  };

  // ===== FEATURED HANDLERS =====
  const saveFeatured = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/superadmin/featured`, featuredForm, { headers });
      toast.success('Property featured');
      setShowFeaturedDialog(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const deleteFeatured = async (id) => {
    try { await axios.delete(`${API}/superadmin/featured/${id}`, { headers }); toast.success('Removed'); fetchData(); }
    catch { toast.error('Failed'); }
  };

  const filteredProps = properties.filter(p =>
    !propertySearch || p.title.toLowerCase().includes(propertySearch.toLowerCase()) || p.city.toLowerCase().includes(propertySearch.toLowerCase())
  );

  const totalClicks = ads.reduce((a, b) => a + (b.clicks || 0), 0);
  const totalImpressions = ads.reduce((a, b) => a + (b.impressions || 0), 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="superadmin-page">
      <h1 className="font-heading text-2xl md:text-3xl font-semibold text-slate-900 mb-2">
        {language === 'es' ? 'Panel Super Admin' : 'Super Admin Panel'}
      </h1>
      <p className="text-slate-500 text-sm mb-6">{language === 'es' ? 'Gestiona publicidad, campanas y propiedades destacadas' : 'Manage ads, campaigns and featured properties'}</p>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        {[
          { label: language === 'es' ? 'Anuncios Activos' : 'Active Ads', value: ads.filter(a => a.is_active).length, icon: Megaphone, color: 'text-blue-600 bg-blue-50' },
          { label: language === 'es' ? 'Campanas' : 'Campaigns', value: campaigns.length, icon: BarChart3, color: 'text-purple-600 bg-purple-50' },
          { label: language === 'es' ? 'Total Clicks' : 'Total Clicks', value: totalClicks, icon: MousePointerClick, color: 'text-accent bg-orange-50' },
          { label: language === 'es' ? 'Destacadas' : 'Featured', value: featured.filter(f => f.is_active).length, icon: Star, color: 'text-yellow-600 bg-yellow-50' },
        ].map((s, i) => (
          <Card key={i}><CardContent className="p-3 flex items-center gap-2.5">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${s.color}`}><s.icon className="w-4 h-4" /></div>
            <div><div className="font-heading text-lg font-bold">{s.value}</div><div className="text-[11px] text-slate-500">{s.label}</div></div>
          </CardContent></Card>
        ))}
      </div>

      <Tabs defaultValue="ads" className="space-y-4">
        <TabsList className="bg-slate-100 p-1 rounded-xl" data-testid="superadmin-tabs">
          <TabsTrigger value="ads" className="rounded-lg gap-1.5"><Megaphone className="w-4 h-4" />{language === 'es' ? 'Anuncios' : 'Ad Banners'}</TabsTrigger>
          <TabsTrigger value="campaigns" className="rounded-lg gap-1.5"><BarChart3 className="w-4 h-4" />{language === 'es' ? 'Campanas' : 'Campaigns'}</TabsTrigger>
          <TabsTrigger value="featured" className="rounded-lg gap-1.5"><Star className="w-4 h-4" />{language === 'es' ? 'Destacadas' : 'Featured'}</TabsTrigger>
        </TabsList>

        {/* ===== ADS TAB ===== */}
        <TabsContent value="ads">
          <div className="flex justify-end mb-4">
            <Button className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] gap-1.5" onClick={() => openAdDialog()} data-testid="create-ad-btn">
              <PlusCircle className="w-4 h-4" />{language === 'es' ? 'Crear Anuncio' : 'Create Ad'}
            </Button>
          </div>

          <div className="bg-white rounded-xl border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{language === 'es' ? 'Anuncio' : 'Ad'}</TableHead>
                  <TableHead>{language === 'es' ? 'Posicion' : 'Position'}</TableHead>
                  <TableHead>{language === 'es' ? 'Negocio' : 'Business'}</TableHead>
                  <TableHead>{language === 'es' ? 'Campana' : 'Campaign'}</TableHead>
                  <TableHead>Clicks</TableHead>
                  <TableHead>{language === 'es' ? 'Impresiones' : 'Impressions'}</TableHead>
                  <TableHead>CTR</TableHead>
                  <TableHead>{language === 'es' ? 'Activo' : 'Active'}</TableHead>
                  <TableHead>{language === 'es' ? 'Acciones' : 'Actions'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ads.map(ad => {
                  const ctr = ad.impressions > 0 ? ((ad.clicks / ad.impressions) * 100).toFixed(1) : '0.0';
                  const camp = campaigns.find(c => c.id === ad.campaign_id);
                  return (
                    <TableRow key={ad.id} data-testid={`ad-row-${ad.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {ad.image_url ? (
                            <div className="w-10 h-10 rounded bg-slate-100 overflow-hidden"><img src={ad.image_url.startsWith('http') ? ad.image_url : `${API}/files/${ad.image_url}`} alt="" className="w-full h-full object-cover" /></div>
                          ) : (
                            <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center"><Megaphone className="w-4 h-4 text-primary" /></div>
                          )}
                          <span className="font-medium text-sm max-w-[150px] truncate">{ad.title}</span>
                        </div>
                      </TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{AD_POSITIONS.find(p => p.value === ad.position)?.label || ad.position}</Badge></TableCell>
                      <TableCell className="text-sm">{ad.business_name || '-'}</TableCell>
                      <TableCell className="text-xs text-slate-500">{camp?.name || '-'}</TableCell>
                      <TableCell><span className="flex items-center gap-1 text-sm font-semibold text-accent"><MousePointerClick className="w-3.5 h-3.5" />{ad.clicks}</span></TableCell>
                      <TableCell><span className="flex items-center gap-1 text-sm text-slate-500"><Eye className="w-3.5 h-3.5" />{ad.impressions}</span></TableCell>
                      <TableCell><span className="text-sm font-medium">{ctr}%</span></TableCell>
                      <TableCell><Badge className={ad.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}>{ad.is_active ? 'Active' : 'Off'}</Badge></TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openAdDialog(ad)} data-testid={`edit-ad-${ad.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => deleteAd(ad.id)} data-testid={`delete-ad-${ad.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {ads.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-500">{language === 'es' ? 'No hay anuncios' : 'No ads yet'}</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>

          {/* Ad Dialog */}
          <Dialog open={showAdDialog} onOpenChange={setShowAdDialog}>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>{editingAd ? (language === 'es' ? 'Editar Anuncio' : 'Edit Ad') : (language === 'es' ? 'Crear Anuncio' : 'Create Ad')}</DialogTitle></DialogHeader>
              <form onSubmit={saveAd} className="space-y-3">
                <div><Label>{language === 'es' ? 'Titulo' : 'Title'}</Label><Input value={adForm.title} onChange={e => setAdForm(f => ({...f, title: e.target.value}))} required className="mt-1" data-testid="ad-title-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>{language === 'es' ? 'Posicion' : 'Position'}</Label>
                    <Select value={adForm.position} onValueChange={v => setAdForm(f => ({...f, position: v}))}>
                      <SelectTrigger className="mt-1" data-testid="ad-position-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{AD_POSITIONS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>{language === 'es' ? 'Campana' : 'Campaign'}</Label>
                    <Select value={adForm.campaign_id || 'none'} onValueChange={v => setAdForm(f => ({...f, campaign_id: v === 'none' ? '' : v}))}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="None" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{language === 'es' ? 'Ninguna' : 'None'}</SelectItem>
                        {campaigns.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>{language === 'es' ? 'Negocio' : 'Business Name'}</Label><Input value={adForm.business_name} onChange={e => setAdForm(f => ({...f, business_name: e.target.value}))} className="mt-1" /></div>
                  <div><Label>Email</Label><Input value={adForm.business_email} onChange={e => setAdForm(f => ({...f, business_email: e.target.value}))} className="mt-1" /></div>
                </div>
                <div><Label>{language === 'es' ? 'URL del Enlace' : 'Link URL'}</Label><Input value={adForm.link_url} onChange={e => setAdForm(f => ({...f, link_url: e.target.value}))} className="mt-1" placeholder="https://" /></div>
                <div>
                  <Label>{language === 'es' ? 'Imagen del Banner' : 'Banner Image'}</Label>
                  <div className="mt-1 flex gap-2 items-center">
                    <label className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-slate-300 cursor-pointer hover:border-primary/50 text-sm text-slate-500">
                      <Image className="w-4 h-4" />{language === 'es' ? 'Subir imagen' : 'Upload image'}
                      <input type="file" accept="image/*" onChange={handleAdImageUpload} className="hidden" />
                    </label>
                    {adForm.image_url && <Badge variant="outline" className="text-xs text-green-600">Uploaded</Badge>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={adForm.is_active} onCheckedChange={v => setAdForm(f => ({...f, is_active: v}))} data-testid="ad-active-switch" />
                  <Label>{language === 'es' ? 'Activo' : 'Active'}</Label>
                </div>
                <Button type="submit" className="w-full bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C]" data-testid="save-ad-btn">
                  {editingAd ? (language === 'es' ? 'Guardar Cambios' : 'Save Changes') : (language === 'es' ? 'Crear Anuncio' : 'Create Ad')}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* ===== CAMPAIGNS TAB ===== */}
        <TabsContent value="campaigns">
          <div className="flex justify-end mb-4">
            <Dialog open={showCampaignDialog} onOpenChange={setShowCampaignDialog}>
              <DialogTrigger asChild>
                <Button className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] gap-1.5" data-testid="create-campaign-btn">
                  <PlusCircle className="w-4 h-4" />{language === 'es' ? 'Crear Campana' : 'Create Campaign'}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{language === 'es' ? 'Nueva Campana' : 'New Campaign'}</DialogTitle></DialogHeader>
                <form onSubmit={saveCampaign} className="space-y-3">
                  <div><Label>{language === 'es' ? 'Nombre' : 'Name'}</Label><Input value={campaignForm.name} onChange={e => setCampaignForm(f => ({...f, name: e.target.value}))} required className="mt-1" data-testid="campaign-name-input" /></div>
                  <div><Label>{language === 'es' ? 'Descripcion' : 'Description'}</Label><Textarea value={campaignForm.description} onChange={e => setCampaignForm(f => ({...f, description: e.target.value}))} className="mt-1" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>{language === 'es' ? 'Negocio' : 'Business'}</Label><Input value={campaignForm.business_name} onChange={e => setCampaignForm(f => ({...f, business_name: e.target.value}))} className="mt-1" /></div>
                    <div><Label>Email</Label><Input value={campaignForm.business_email} onChange={e => setCampaignForm(f => ({...f, business_email: e.target.value}))} className="mt-1" /></div>
                  </div>
                  <Button type="submit" className="w-full bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C]" data-testid="save-campaign-btn">{language === 'es' ? 'Crear' : 'Create'}</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {campaigns.map(camp => (
              <Card key={camp.id} data-testid={`campaign-card-${camp.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center"><BarChart3 className="w-5 h-5 text-purple-600" /></div>
                      <div><h3 className="font-semibold">{camp.name}</h3><p className="text-xs text-slate-500">{camp.business_name || '-'}</p></div>
                    </div>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => deleteCampaign(camp.id)} data-testid={`delete-campaign-${camp.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                  {camp.description && <p className="text-sm text-slate-600 line-clamp-2 mb-2">{camp.description}</p>}
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="text-xs gap-1"><Megaphone className="w-3 h-3" />{camp.ad_count || 0} ads</Badge>
                    <Badge className={camp.is_active ? 'bg-green-100 text-green-700 text-xs' : 'bg-slate-100 text-slate-500 text-xs'}>{camp.is_active ? 'Active' : 'Inactive'}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
            {campaigns.length === 0 && <p className="text-slate-500 col-span-2 text-center py-8">{language === 'es' ? 'No hay campanas' : 'No campaigns yet'}</p>}
          </div>
        </TabsContent>

        {/* ===== FEATURED TAB ===== */}
        <TabsContent value="featured">
          <div className="flex justify-end mb-4">
            <Dialog open={showFeaturedDialog} onOpenChange={setShowFeaturedDialog}>
              <DialogTrigger asChild>
                <Button className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] gap-1.5" data-testid="promote-property-btn">
                  <Star className="w-4 h-4" />{language === 'es' ? 'Destacar Propiedad' : 'Promote Property'}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>{language === 'es' ? 'Destacar Propiedad' : 'Feature a Property'}</DialogTitle></DialogHeader>
                <form onSubmit={saveFeatured} className="space-y-3">
                  <div>
                    <Label>{language === 'es' ? 'Buscar Propiedad' : 'Search Property'}</Label>
                    <Input placeholder={language === 'es' ? 'Buscar por titulo o ciudad...' : 'Search by title or city...'} value={propertySearch} onChange={e => setPropertySearch(e.target.value)} className="mt-1" data-testid="featured-search" />
                  </div>
                  <div className="max-h-48 overflow-y-auto border rounded-lg">
                    {filteredProps.map(p => (
                      <button type="button" key={p.id} onClick={() => setFeaturedForm(f => ({...f, property_id: p.id}))}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 border-b border-border/50 ${featuredForm.property_id === p.id ? 'bg-primary/5 border-l-2 border-l-primary' : ''}`}
                        data-testid={`select-property-${p.id}`}>
                        <div className="w-8 h-8 rounded bg-slate-100 overflow-hidden flex-shrink-0">
                          <img src={getImageUrl(p.main_photo)} alt="" className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{p.title}</p>
                          <p className="text-xs text-slate-500">{p.city} - {formatPrice(p.price, p.currency, p.operation_type)}</p>
                        </div>
                        {featuredForm.property_id === p.id && <Star className="w-4 h-4 text-yellow-500 flex-shrink-0" />}
                      </button>
                    ))}
                  </div>
                  <div><Label>{language === 'es' ? 'Posicion' : 'Position'}</Label>
                    <Select value={featuredForm.position} onValueChange={v => setFeaturedForm(f => ({...f, position: v}))}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{FEATURED_POSITIONS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" disabled={!featuredForm.property_id} className="w-full bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C]" data-testid="save-featured-btn">
                    {language === 'es' ? 'Destacar' : 'Feature Property'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {featured.length > 0 ? (
            <div className="space-y-3">
              {featured.map(f => (
                <div key={f.id} className="bg-white rounded-xl border-2 border-yellow-200 p-4 flex items-center gap-4" data-testid={`featured-item-${f.id}`}>
                  {f.property ? (
                    <>
                      <div className="w-20 h-16 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                        <img src={getImageUrl(f.property.main_photo)} alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Star className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                          <h3 className="font-semibold text-sm truncate">{f.property.title}</h3>
                        </div>
                        <p className="text-xs text-slate-500">{f.property.city} - {formatPrice(f.property.price, f.property.currency, f.property.operation_type)}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">{FEATURED_POSITIONS.find(p => p.value === f.position)?.label || f.position}</Badge>
                          <Badge className={f.is_active ? 'bg-green-100 text-green-700 text-xs' : 'bg-slate-100 text-slate-500 text-xs'}>{f.is_active ? 'Active' : 'Off'}</Badge>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex-1"><p className="text-sm text-slate-500">Property not found (ID: {f.property_id})</p></div>
                  )}
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => navigate(`/property/${f.property_id}`)}><Eye className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => deleteFeatured(f.id)} data-testid={`remove-featured-${f.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-xl border border-border">
              <Star className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">{language === 'es' ? 'No hay propiedades destacadas' : 'No featured properties yet'}</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
